import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../global/functions/apiClient";
import { getAuthHeaders } from "../global/functions/authorization";
const apiUrl = process.env.REACT_APP_API_URL;
const initialState = {
  histories: [],
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalRecords: 1,
  },
  recents: [],
  status: "idle",
  error: null,
  loading: false,
  // Dedicated status/params for the dashboard's "recent orders" widget —
  // kept separate from `status` above (which the History screen's own
  // pagination uses) so the two screens don't stomp on each other's loading
  // state, and so we can tell whether recents are already fresh for the
  // current restaurant without re-fetching on every navigation.
  recentsStatus: "idle",
  recentsLastRestaurantId: null,
  statistics: null,
  statisticsStatus: "idle",
  statisticsError: null,
  statisticsLastParams: null,
  success: null,
};

export const fetchHistory = createAsyncThunk(
  "history/getAll",
  async ({ restaurantId, search, limit, page, status, packId, methodId, filter = "all", startDate, endDate }) => {
    try {
      const response = await axios.get(`${apiUrl}/history`, {
        ...getAuthHeaders(false, restaurantId),
        params: {
          search,
          limit,
          page,
          ...(status && { status }),
          ...(packId && { packId }),
          ...(methodId && { methodId }),
          filter,
          ...(startDate && { startDate }),
          ...(endDate && { endDate }),
        },
      });

      return response?.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  }
);

export const fetchHistoryForExport = createAsyncThunk(
  "history/export",
  async ({
    restaurantId,
    search = "",
    status,
    packId,
    methodId,
    filter = "all",
    startDate,
    endDate,
  }) => {
    try {
      const response = await axios.get(`${apiUrl}/history`, {
        ...getAuthHeaders(false, restaurantId),
        params: {
          page: 1,
          limit: 100000,
          filter,
          ...(search && { search }),
          ...(status && { status }),
          ...(packId && { packId }),
          ...(methodId && { methodId }),
          ...(startDate && { startDate }),
          ...(endDate && { endDate }),
        },
      });
      return response?.data?.histories || [];
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  }
);

export const fetchAllHistories = createAsyncThunk(
  "history/getAllHistories",
  async ({ restaurantId, search = "", limit = 10,filter = "all", page = 1 }) => {
    try {
      const response = await axios.get(`${apiUrl}/history?page=1&limit=100000`, {
        ...getAuthHeaders(false, restaurantId),
        params: {
          search,
          page,
        },
      });
      return response?.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  }
);


export const fetchRecentHistories = createAsyncThunk(
  "history/getRecentHistories",
  async (restaurantId) => {
    try {
      const response = await axios.get(
        `${apiUrl}/history/10`,
        getAuthHeaders(false, restaurantId)
      );
      return response?.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  }
);

export const deleteHistory = createAsyncThunk(
  "history/delete",
  async ({ id, restaurantId }, { rejectWithValue }) => {
    try {
      const response = await axios.delete(
        `${apiUrl}/history/${id}`,
        getAuthHeaders(false, restaurantId)
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const fetchStatistics = createAsyncThunk(
  "history/fetchStatistics",
  async ({ restaurantId, filter = "all", startDate, endDate }) => {
    try {
      let url = `${apiUrl}/history/stats?filter=${filter}`;
      if (filter === "custom" && startDate && endDate) {
        url += `&startDate=${encodeURIComponent(
          startDate
        )}&endDate=${encodeURIComponent(endDate)}`;
      }
      const response = await axios.get(
        url,
        getAuthHeaders(false, restaurantId)
      );
      return response?.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  }
);

const historySlice = createSlice({
  name: "history",
  initialState,
  reducers: {
    updateStatus: (state) => {
      state.status = "idle";
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchHistory.pending, (state, action) => {
        state.status = "loading";
        state.loading = true;
      })
      .addCase(fetchHistory.fulfilled, (state, action) => {
        state.status = "fetchData";
        state.loading = false;
        state.histories = action.payload.histories || [];
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchHistory.rejected, (state, action) => {
        state.status = "fetchError";
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchAllHistories.pending, (state, action) => {
        state.status = "loading";
        state.loading = true;
      })
      .addCase(fetchAllHistories.fulfilled, (state, action) => {
        state.status = "fetchAllData";
        state.loading = false;
        state.histories = action.payload.histories || [];
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchAllHistories.rejected, (state, action) => {
        state.status = "fetchAllError";
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchRecentHistories.pending, (state, action) => {
        state.recentsStatus = "loading";
      })
      .addCase(fetchRecentHistories.fulfilled, (state, action) => {
        state.recentsStatus = "fetchRecentsData";
        state.recents = action.payload;
        state.recentsLastRestaurantId = action.meta.arg;
      })
      .addCase(fetchRecentHistories.rejected, (state, action) => {
        state.recentsStatus = "fetchRecentsError";
        state.error = action.error.message;
      })
      .addCase(fetchStatistics.pending, (state) => {
        state.statisticsStatus = "loading";
        state.statisticsError = null;
      })
      .addCase(fetchStatistics.fulfilled, (state, action) => {
        state.statisticsStatus = "succeeded";
        state.statistics = action.payload;
        state.statisticsLastParams = JSON.stringify(action.meta.arg);
      })
      .addCase(fetchStatistics.rejected, (state, action) => {
        state.statisticsStatus = "failed";
        state.statisticsError = action.error.message;
      })
      .addCase(deleteHistory.fulfilled, (state, action) => {
        const id = action.meta.arg.id;
        state.histories = state.histories.filter((item) => item._id !== id);
        state.recents = state.recents.filter((item) => item._id !== id);
        state.pagination.totalRecords = Math.max(
          0,
          (state.pagination.totalRecords || 0) - 1
        );
        state.status = "deleteSuccess";
        state.success = action.payload?.message;
      })
      .addCase(deleteHistory.rejected, (state, action) => {
        state.status = "deleteError";
        state.error = action.payload || action.error.message;
      });
  },
});

export const { updateStatus } = historySlice.actions;
export const getHistorySuccess = (state) => state.history.success;
export const selectAllHistory = (state) => state.history.histories;
export const selectPagination = (state) => state.history.pagination;
export const getHistoryStatus = (state) => state.history.status;
export const getHistoryError = (state) => state.history.error;

export const selectRecentHistory = (state) => state.history.recents;
export const getRecentHistoryStatus = (state) => state.history.recentsStatus;
export const getRecentHistoryError = (state) => state.history.error;
export const selectRecentsLastRestaurantId = (state) =>
  state.history.recentsLastRestaurantId;

// Statistics selectors
export const selectStatistics = (state) => state.history.statistics;
export const getStatisticsStatus = (state) => state.history.statisticsStatus;
export const getStatisticsError = (state) => state.history.statisticsError;
export const selectStatisticsLastParams = (state) =>
  state.history.statisticsLastParams;

export default historySlice.reducer;
